<template>
  <div class="nextup-page">
    <div class="top-bar">
      <a class="back-link" href="#" @click.prevent="$emit('back')">← Back to Benevolism</a>
      <div class="top-brand">
        <span class="mark">NW</span>
        BENEVOLISM<span class="top-sep">::</span>ALGO
      </div>
    </div>

    <main>
      <section class="hero">
        <div class="hero-grid-bg" />
        <div class="hero-glow" />
        <v-container class="hero-inner" style="max-width:1180px; position:relative; z-index:1;">
          <div class="cover-col">
            <div class="cover-card">
              <div class="cover-eyebrow">
                <span class="cover-dot"></span>
                SYSTEM ACTIVE
              </div>
              <div class="cover-nw">NW</div>
              <div class="cover-title">NEXTUP<br>WEALTH<br>MANAGEMENT</div>
              <div class="cover-divider"></div>
              <div class="cover-algo">BENEVOLISM<span class="cover-sep">::</span>ALGO</div>
              <div class="cover-divider"></div>
              <div class="cover-engine">ENGINE v4.2.1</div>
              <div class="cover-private">PRIVATE · PROPRIETARY · PERMANENT</div>
            </div>
            <div class="cover-badge">
              <div class="badge-inner">
                <div class="badge-top">BENEVOLISM</div>
                <div class="badge-main">ALGO</div>
                <div class="badge-bottom">ARM</div>
              </div>
            </div>
          </div>

          <div class="info-col">
            <div class="eyebrow">
              <span class="eyebrow-dot"></span>
              Benevolism · Algorithmic Research Arm
            </div>
            <h1 class="info-title">NextUp Wealth Management</h1>
            <p class="info-sub">The machine extracts. We redirect.</p>
            <p class="info-desc">
              NextUp Wealth Management is Benevolism's private algorithmic trading research operation — built to run the current financial system's own instruments, dispassionately and precisely, and aim every dollar of surplus at the philosophy you are reading right now.
            </p>
            <p class="info-desc info-desc--dim">
              The financial system was not designed to fail. It was designed to extract. NextUp exists at that intersection — mastering the tools of the current order to fund the architecture of a better one.
            </p>
            <div class="stats-row">
              <div class="stat">
                <div class="stat-num">100%</div>
                <div class="stat-label">Surplus to Benevolism</div>
              </div>
              <div class="stat-sep"></div>
              <div class="stat">
                <div class="stat-num">0</div>
                <div class="stat-label">Emotional bias</div>
              </div>
              <div class="stat-sep"></div>
              <div class="stat">
                <div class="stat-num">∞</div>
                <div class="stat-label">Horizon</div>
              </div>
            </div>
            <div class="private-badge">
              <span class="private-dot"></span>
              Private · Not open to investors · Research operation only
            </div>
          </div>
        </v-container>
      </section>

      <section class="alt">
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">
            <span class="eyebrow-dot"></span>
            The Engine
          </div>
          <h2 class="heading">How It Works</h2>
          <p class="lede">
            Algorithmic trading removes the single greatest variable in financial decision-making: human emotion. NextUp runs on a set of rules. The rules do not panic. They do not greed. They do not tire.
          </p>

          <div class="terminal-full">
            <div class="terminal-bar">
              <span class="t-dot t-red"></span>
              <span class="t-dot t-amber"></span>
              <span class="t-dot t-green"></span>
              <span class="terminal-title">benevolism_engine.py — NextUp Wealth Management</span>
              <span class="t-live"><span class="t-live-dot"></span>LIVE</span>
            </div>
            <div class="terminal-body">
              <div class="t-line" v-for="(line, i) in terminalLines" :key="i" :class="line.type">
                <span class="t-prefix">{{ line.prefix }}</span>
                <span class="t-text">{{ line.text }}</span>
              </div>
              <div class="t-cursor">█</div>
            </div>
          </div>

          <div class="pillars-grid">
            <div class="pillar" v-for="(p, i) in pillars" :key="i">
              <div class="pillar-glyph">{{ p.glyph }}</div>
              <h3 class="pillar-title">{{ p.title }}</h3>
              <p class="pillar-text">{{ p.text }}</p>
            </div>
          </div>
        </v-container>
      </section>

      <section>
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">
            <span class="eyebrow-dot"></span>
            The Diagnosis
          </div>
          <h2 class="heading">What the Current System Costs</h2>
          <p class="lede">
            The financial system does not fail by accident. It extracts by design — through fee structures, information asymmetry, and dependency engineering. These are not abstract figures. They are the cost paid by real people.
          </p>
          <div class="ledger-grid">
            <div class="ledger-item" v-for="item in ledger" :key="item.label">
              <div class="ledger-fig" :style="{ color: item.color }">{{ item.figure }}</div>
              <div class="ledger-label">{{ item.label }}</div>
              <div class="ledger-note">{{ item.note }}</div>
              <div class="ledger-bar-track">
                <div class="ledger-bar-fill" :style="{ width: item.pct + '%', background: item.color }"></div>
              </div>
            </div>
          </div>

          <div class="diagnosis-quote">
            <div class="dq-mark">"</div>
            <blockquote>The most powerful force in finance is not compound interest. It is compound extraction — and it has been working against ordinary people for generations.</blockquote>
            <div class="dq-attr">— Enrico Michael Severe, The Benevolent Bank</div>
          </div>
        </v-container>
      </section>

      <section class="alt">
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">
            <span class="eyebrow-dot"></span>
            The Five Laws
          </div>
          <h2 class="heading">Benevolism Applied to Finance</h2>
          <p class="lede">
            The Benevolism framework applied to financial services yields five binding laws — the standard against which every financial institution, product, and system should be measured.
          </p>
          <div class="laws-grid">
            <div class="law-card" v-for="(law, i) in laws" :key="i">
              <div class="law-numeral">{{ law.numeral }}</div>
              <div>
                <div class="law-name">{{ law.name }}</div>
                <p class="law-desc">{{ law.desc }}</p>
              </div>
            </div>
          </div>
        </v-container>
      </section>

      <section>
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">
            <span class="eyebrow-dot"></span>
            The Mission
          </div>
          <h2 class="heading">Why Benevolism Runs an Algo Firm</h2>
          <p class="lede">
            You cannot redesign a system you do not understand. You cannot fund a movement without capital. NextUp is both the research tool and the funding mechanism — a machine that learns the current order from the inside while building the resources to replace it.
          </p>
          <div class="mission-cards">
            <div class="mission-card" v-for="(m, i) in mission" :key="i">
              <div class="mission-num">{{ String(i + 1).padStart(2, '0') }}</div>
              <div>
                <div class="mission-title">{{ m.title }}</div>
                <p class="mission-text">{{ m.text }}</p>
              </div>
            </div>
          </div>
        </v-container>
      </section>

      <!-- ═══════════════════════════════════════════════════════ -->
      <!--  REACTIVE RESEARCH SYSTEMS                             -->
      <!-- ═══════════════════════════════════════════════════════ -->

      <section class="alt rrs-section">
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">
            <span class="rrs-eyebrow-dot"></span>
            Intelligence Infrastructure
          </div>
          <h2 class="heading">Reactive Research Systems</h2>
          <p class="lede rrs-lede">
            The operational intelligence arm of NextUp Wealth Management.
            Not a product. Not a platform. A private instrument built to see what others cannot.
          </p>

          <div class="rrs-grid">
            <div class="rrs-classified-panel">
              <div class="rrs-panel-label">SYSTEM FILE</div>
              <div class="rrs-redact-rows">
                <div class="rrs-redact-row" v-for="row in rrsRedacted" :key="row.label">
                  <span class="rrs-redact-key">{{ row.label }}</span>
                  <div class="rrs-redact-bars">
                    <span
                      v-for="(w, bi) in row.bars"
                      :key="bi"
                      class="rrs-redact-bar"
                      :style="{ width: w + 'px' }"
                    ></span>
                  </div>
                </div>
              </div>
              <div class="rrs-panel-stamp">CLASSIFIED</div>
            </div>

            <div class="rrs-caps">
              <div class="rrs-cap" v-for="cap in rrsCapabilities" :key="cap.label">
                <div class="rrs-cap-glyph">{{ cap.glyph }}</div>
                <div>
                  <div class="rrs-cap-label">{{ cap.label }}</div>
                  <p class="rrs-cap-text">{{ cap.text }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="terminal-full rrs-terminal">
            <div class="terminal-bar">
              <span class="t-dot t-red"></span>
              <span class="t-dot t-amber"></span>
              <span class="t-dot t-green"></span>
              <span class="terminal-title">rrs_core.py — Reactive Research Systems · [ACCESS RESTRICTED]</span>
              <span class="t-live t-live--dim"><span class="t-live-dot t-live-dot--dim"></span>CLASSIFIED</span>
            </div>
            <div class="terminal-body">
              <div class="t-line" v-for="(line, i) in rrsTerminalLines" :key="i" :class="line.type">
                <span class="t-prefix">{{ line.prefix }}</span>
                <span class="t-text">{{ line.text }}</span>
              </div>
              <div class="t-cursor">█</div>
            </div>
          </div>

          <div class="rrs-closing-note">
            Reactive Research Systems is proprietary to NextUp Wealth Management.
            No licensing. No partnerships. No exceptions.
          </div>
        </v-container>
      </section>

      <!-- ═══════════════════════════════════════════════════════ -->

      <section class="closing">
        <v-container style="max-width:780px; position:relative; z-index:1;">
          <div class="closing-glow"></div>
          <div class="closing-eyebrow">
            <span class="eyebrow-dot"></span>
            PRIVATE · PROPRIETARY · PERMANENT
          </div>
          <h2 class="closing-title">
            The work speaks<br>when it is ready.
          </h2>
          <p class="closing-body">
            NextUp Wealth Management is not seeking partners, investors, or attention. It is a research operation with a long horizon. The returns fund the doctrine. The doctrine funds the future.
          </p>
          <div class="closing-actions">
            <a href="https://nextupwealthmanagement.com" target="_blank" class="btn-teal">
              Visit nextupwealthmanagement.com ↗
            </a>
            <button class="btn-ghost-teal" @click="$emit('back')">
              ← Back to Benevolism
            </button>
          </div>
        </v-container>
      </section>
    </main>

    <footer class="page-footer">
      <div class="footer-text">
        © {{ new Date().getFullYear() }} Enrico Michael Severe ·
        <a href="#" @click.prevent="$emit('back')">benevolism.com</a> ·
        <a href="https://nextupwealthmanagement.com" target="_blank">nextupwealthmanagement.com</a>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'NextUpView',
  emits: ['back'],
  data: () => ({
    terminalLines: [
      { prefix: '>>>', text: 'import benevolism as bvl', type: 'cmd' },
      { prefix: '...', text: 'engine = bvl.AlgoEngine(mode="impact", surplus_routing="benevolism.fund()")', type: 'cmd' },
      { prefix: '[✓]', text: 'Strategy loaded: community_first_v3', type: 'success' },
      { prefix: '[✓]', text: 'Execution model: zero-emotion, full-precision', type: 'success' },
      { prefix: '[✓]', text: 'Risk framework: ACTIVE', type: 'success' },
      { prefix: '[→]', text: 'Surplus routing: benevolism.fund(target="financial_extraction_margin_zero")', type: 'info' },
      { prefix: '[✓]', text: 'External investor access: NONE', type: 'success' },
      { prefix: '[→]', text: 'Horizon: indefinite', type: 'info' },
      { prefix: '[✓]', text: 'Benevolism protocol: ACTIVE', type: 'success' },
      { prefix: '...', text: 'engine.run()', type: 'cmd' },
    ],
    pillars: [
      {
        glyph: 'Σ',
        title: 'Precision over intuition',
        text: 'Every trade is governed by rules, not feelings. The algorithm executes with the same cold consistency whether markets are rising, collapsing, or flat. Human emotion is the primary source of financial loss. We eliminate it entirely.',
      },
      {
        glyph: 'λ',
        title: 'Scale without extraction',
        text: 'The goal is not to accumulate. It is to generate enough surplus, consistently enough, that Benevolism can fund its research, infrastructure, and expansion without dependence on outside capital or ideological compromise.',
      },
      {
        glyph: '∂',
        title: 'Inside knowledge, outside mission',
        text: 'You cannot disrupt a system you do not understand. Operating inside the current financial order — learning its mechanics, its failure points, its leverage — gives Benevolism knowledge that cannot be gained from the outside.',
      },
      {
        glyph: '∇',
        title: 'Private by design',
        text: 'NextUp is not a fund. It does not take investor capital. It does not publish returns. It is a private research operation whose outputs are measured in the health of the movement it funds, not in AUM.',
      },
    ],
    ledger: [
      { figure: '$12B+', label: 'Overdraft fees collected annually', note: 'From the customers least able to afford them. Not from cost — from market power over captive account holders.', color: '#ff003c', pct: 90 },
      { figure: '300%+', label: 'Effective APR on payday loans', note: 'The cost of $500 in credit for someone with no alternative. A fee structure designed not to help but to trap.', color: '#ff003c', pct: 85 },
      { figure: '~$190K', label: 'Lost per retiree to fund fees', note: 'The compound cost of a 1% annual management fee over 30 years, versus a 0.1% index fund. Hidden, legal, devastating.', color: '#ffd60a', pct: 65 },
      { figure: '28%', label: 'US households underbanked', note: 'Excluded from or underserved by mainstream financial systems. Pushed into a shadow economy that charges triple.', color: '#ffd60a', pct: 55 },
      { figure: '23%', label: 'Average credit card APR', note: 'Multiples above the cost of funds. Priced not at genuine risk but at what a captive borrower will absorb.', color: '#ffd60a', pct: 50 },
      { figure: '$1.7T', label: 'Student loan debt outstanding', note: 'Financing the highest-return investment most people make — at rates that reflect captive demand, not genuine risk.', color: '#ff003c', pct: 95 },
    ],
    laws: [
      { numeral: 'I', name: 'Cost-based pricing', desc: 'Every financial product must be priced at the genuine cost of delivering it plus a fair return. Any charge above this is extraction, not service.' },
      { numeral: 'II', name: 'Universal financial access', desc: 'Basic financial services — a safe place to keep money, a means of payment, affordable credit — are infrastructure. Financial exclusion is a design failure.' },
      { numeral: 'III', name: 'Total cost transparency', desc: 'Every product must disclose its total dollar cost over the realistic life of the product. The gap between advertised and experienced cost is where extraction lives.' },
      { numeral: 'IV', name: 'Anti-exploitation', desc: 'Products may not be designed to exploit cognitive limitations, financial desperation, or captive position. The standard is not legality — it is whether the product genuinely serves the person who buys it.' },
      { numeral: 'V', name: 'Incentive alignment', desc: 'Financial institutions must earn more when their customers accumulate more wealth — not when their customers pay more fees. Compensation that rewards harm must be reformed.' },
    ],
    mission: [
      {
        title: 'Understand the system from the inside',
        text: 'The mechanics of the current financial order — its extraction points, its leverage, its failure modes — are not visible from the outside. NextUp operates within them, generating knowledge that informs the Benevolism framework directly.',
      },
      {
        title: 'Generate independent funding for the doctrine',
        text: 'A philosophy that depends on outside capital is a philosophy that can be purchased. NextUp gives Benevolism an independent funding mechanism — surplus generated by the system it seeks to replace.',
      },
      {
        title: 'Demonstrate that the alternative works',
        text: 'Every dollar generated by NextUp and redirected to Benevolism is a proof of concept: that capital can be created without extraction, and deployed toward human flourishing rather than accumulation.',
      },
      {
        title: 'Build with a long horizon',
        text: 'Benevolism is not an overnight project. The transformation of economic systems takes time. NextUp is structured to fund that horizon — not to maximize returns in the next quarter.',
      },
    ],
    rrsRedacted: [
      { label: 'SYSTEM DESIGNATION', bars: [148] },
      { label: 'ARCHITECTURE',       bars: [96, 72] },
      { label: 'DATA SOURCES',       bars: [120, 56, 80] },
      { label: 'COVERAGE SCOPE',     bars: [104] },
      { label: 'MODEL VERSION',      bars: [88, 44] },
    ],
    rrsCapabilities: [
      {
        glyph: '◎',
        label: 'Geopolitical pattern recognition',
        text: 'Continuous monitoring of sovereign-level dynamics across every major economy. The signal surface is vast. The noise floor is managed.',
      },
      {
        glyph: '⊕',
        label: 'Presidential cycle modeling',
        text: 'Decades of market behavior mapped against leadership transition data. Historical structure applied forward.',
      },
      {
        glyph: '∿',
        label: 'Earnings & capital flow synthesis',
        text: 'Corporate earnings as a window into macroeconomic health. Not the headline numbers — the structure beneath them.',
      },
      {
        glyph: '⟁',
        label: 'Horizon monitoring',
        text: 'Systems operating at scales the public does not see. Agriculture. Infrastructure. Movement. The full picture, assembled quietly.',
      },
    ],
    rrsTerminalLines: [
      { prefix: '>>>', text: 'rrs.initialize(scope="global", depth=■■■■■■)', type: 'cmd' },
      { prefix: '[✓]', text: 'Signal acquisition: active', type: 'success' },
      { prefix: '[✓]', text: 'Pattern corpus: loaded', type: 'success' },
      { prefix: '[■]', text: 'Model designation: [REDACTED]', type: 'redacted' },
      { prefix: '[■]', text: 'Source topology: [REDACTED]', type: 'redacted' },
      { prefix: '[→]', text: 'Output routing: internal only', type: 'info' },
      { prefix: '[—]', text: 'Public interface: none', type: 'dim' },
    ],
  }),
  mounted () {
    window.scrollTo({ top: 0 })
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@300;400;500&family=Inter:wght@300;400;500;600;700&family=Playfair+Display:wght@400;600;700;900&display=swap');

.nextup-page {
  background: #030b0e;
  color: #e2f4f0;
  font-family: 'Inter', sans-serif;
  min-height: 100vh;
}

.top-bar {
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(3,11,14,0.92);
  backdrop-filter: blur(8px);
  border-bottom: 0.5px solid rgba(0,245,212,0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 32px;
}
.back-link {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.06em;
  color: rgba(0,245,212,0.6);
  text-decoration: none;
  transition: color 0.2s;
  cursor: pointer;
}
.back-link:hover { color: #00f5d4; }
.top-brand {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.14em;
  color: rgba(226,244,240,0.45);
  display: flex;
  align-items: center;
  gap: 10px;
}
.top-brand .mark {
  width: 24px; height: 24px;
  background: rgba(0,245,212,0.1);
  border: 0.5px solid rgba(0,245,212,0.3);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 500;
  color: #00f5d4;
}
.top-sep { color: rgba(0,245,212,0.4); }

.eyebrow {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 400;
  letter-spacing: 0.18em;
  color: rgba(0,245,212,0.6);
  text-transform: uppercase;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
}
.eyebrow-dot {
  width: 5px; height: 5px;
  border-radius: 50%;
  background: #00f5d4;
  flex-shrink: 0;
  animation: dotPulse 2s infinite;
}
@keyframes dotPulse {
  0%,100% { opacity:1; box-shadow: 0 0 0 0 rgba(0,245,212,0.4); }
  50% { opacity:0.7; box-shadow: 0 0 0 5px rgba(0,245,212,0); }
}

.heading {
  font-family: 'Playfair Display', serif;
  font-size: clamp(28px,4vw,44px);
  font-weight: 700;
  color: #e2f4f0;
  line-height: 1.18;
  margin-bottom: 20px;
  text-align: center;
}
.lede {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  line-height: 1.8;
  color: rgba(226,244,240,0.55);
  font-weight: 300;
  text-align: center;
  max-width: 680px;
  margin: 0 auto 56px;
}

.hero {
  padding: 72px 0 96px;
  position: relative;
  overflow: hidden;
}
.hero-grid-bg {
  position: absolute; inset: 0;
  background-image: linear-gradient(rgba(0,245,212,0.03) 1px,transparent 1px), linear-gradient(90deg,rgba(0,245,212,0.03) 1px,transparent 1px);
  background-size: 52px 52px;
  pointer-events: none;
}
.hero-glow {
  position: absolute; top:-200px; right:-200px;
  width:700px; height:700px;
  background: radial-gradient(circle,rgba(0,245,212,0.06) 0%,transparent 70%);
  pointer-events: none;
}
.hero-inner {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 72px;
  align-items: start;
}
@media (max-width:900px) {
  .hero-inner { grid-template-columns:1fr; }
  .cover-col { display:flex; flex-direction:column; align-items:center; }
}

.cover-col { position: relative; }
.cover-card {
  width: 220px;
  min-height: 320px;
  background: linear-gradient(160deg,#050f12 0%,#030b0e 60%,#020709 100%);
  border: 0.5px solid rgba(0,245,212,0.3);
  box-shadow: 0 0 0 1px rgba(0,245,212,0.06), 8px 8px 32px rgba(0,0,0,0.7), inset 0 0 60px rgba(0,245,212,0.02);
  padding: 28px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  transform: rotate(-2deg);
  transition: transform 0.3s;
  position: relative;
}
.cover-card::before {
  content:'';
  position:absolute; inset:5px;
  border: 0.5px solid rgba(0,245,212,0.1);
  pointer-events:none;
}
.cover-card:hover { transform: rotate(0deg) scale(1.02); }
.cover-eyebrow {
  font-family: 'JetBrains Mono', monospace;
  font-size: 8px;
  letter-spacing: 0.14em;
  color: rgba(0,245,212,0.5);
  display: flex;
  align-items: center;
  gap: 6px;
}
.cover-dot {
  width:4px; height:4px; border-radius:50%;
  background: #00f5d4;
  animation: dotPulse 2s infinite;
}
.cover-nw {
  font-family: 'JetBrains Mono', monospace;
  font-size: 36px;
  font-weight: 500;
  color: #00f5d4;
  line-height: 1;
  letter-spacing: 0.05em;
}
.cover-title {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #e2f4f0;
  letter-spacing: 0.06em;
  line-height: 1.4;
}
.cover-divider {
  width: 80%; height: 0.5px;
  background: linear-gradient(90deg,transparent,rgba(0,245,212,0.4),transparent);
}
.cover-algo {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: #00f5d4;
  letter-spacing: 0.1em;
}
.cover-sep { color: rgba(0,245,212,0.35); }
.cover-engine {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  color: rgba(226,244,240,0.3);
  letter-spacing: 0.14em;
}
.cover-private {
  font-family: 'JetBrains Mono', monospace;
  font-size: 7px;
  color: rgba(226,244,240,0.2);
  letter-spacing: 0.1em;
  line-height: 1.6;
}
.cover-badge {
  position: absolute;
  top: -12px; right: -12px;
  width: 68px; height: 68px;
  background: #00f5d4;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  transform: rotate(15deg);
}
.badge-inner { color: #030b0e; }
.badge-top { font-family:'Inter',sans-serif; font-size:6px; font-weight:700; letter-spacing:0.08em; }
.badge-main { font-family:'JetBrains Mono',monospace; font-size:13px; font-weight:500; line-height:1.1; }
.badge-bottom { font-family:'Inter',sans-serif; font-size:7px; font-weight:600; }

.info-col { padding-top: 4px; }
.info-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(28px,3.5vw,48px);
  font-weight: 700;
  color: #e2f4f0;
  margin-bottom: 8px;
  line-height: 1.15;
}
.info-sub {
  font-family: 'JetBrains Mono', monospace;
  font-size: 14px;
  color: #00f5d4;
  margin-bottom: 22px;
  letter-spacing: 0.04em;
}
.info-desc {
  font-size: 15px;
  line-height: 1.8;
  color: rgba(226,244,240,0.65);
  margin-bottom: 16px;
  font-weight: 300;
  max-width: 580px;
}
.info-desc--dim { color: rgba(226,244,240,0.38); font-size: 14px; }
.stats-row {
  display: flex;
  align-items: center;
  margin: 32px 0;
  border: 0.5px solid rgba(0,245,212,0.15);
  width: fit-content;
}
.stat { padding: 16px 28px; text-align: center; }
.stat-num {
  font-family: 'JetBrains Mono', monospace;
  font-size: 28px;
  font-weight: 500;
  color: #00f5d4;
  line-height: 1;
  margin-bottom: 4px;
}
.stat-label {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  letter-spacing: 0.1em;
  color: rgba(226,244,240,0.35);
  text-transform: uppercase;
}
.stat-sep {
  width: 0.5px; height: 56px;
  background: rgba(0,245,212,0.12);
  flex-shrink: 0;
}
.private-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(0,245,212,0.05);
  border: 0.5px solid rgba(0,245,212,0.2);
  padding: 10px 18px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: rgba(0,245,212,0.6);
  letter-spacing: 0.08em;
}
.private-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #00f5d4;
  animation: dotPulse 2s infinite;
  flex-shrink: 0;
}

section { padding: 96px 0; }
section.alt {
  background: #050f12;
  border-top: 0.5px solid rgba(0,245,212,0.08);
  border-bottom: 0.5px solid rgba(0,245,212,0.08);
}

.terminal-full {
  background: #050f12;
  border: 0.5px solid rgba(0,245,212,0.18);
  border-radius: 6px;
  overflow: hidden;
  font-family: 'JetBrains Mono', monospace;
  margin-bottom: 56px;
}
.terminal-bar {
  display: flex; align-items: center; gap: 6px;
  padding: 12px 18px;
  background: #081418;
  border-bottom: 0.5px solid rgba(0,245,212,0.08);
}
.t-dot { width:10px; height:10px; border-radius:50%; }
.t-red { background:#ff5f57; }
.t-amber { background:#febc2e; }
.t-green { background:#28c840; }
.terminal-title {
  font-size: 11px;
  color: rgba(226,244,240,0.3);
  margin-left: 8px;
  letter-spacing: 0.04em;
  flex: 1;
}
.t-live {
  display: flex; align-items: center; gap: 5px;
  font-size: 9px;
  color: rgba(0,245,212,0.6);
  letter-spacing: 0.12em;
}
.t-live--dim { color: rgba(226,244,240,0.25); }
.t-live-dot {
  width: 5px; height: 5px; border-radius: 50%;
  background: #00f5d4;
  animation: blink 1.2s step-end infinite;
}
.t-live-dot--dim { background: rgba(226,244,240,0.2); animation: none; }
@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
.terminal-body { padding: 24px 22px; display: flex; flex-direction: column; gap: 10px; }
.t-line { display: flex; gap: 16px; font-size: 12.5px; line-height: 1.5; }
.t-prefix { color: rgba(226,244,240,0.28); min-width: 32px; }
.t-line.cmd .t-text { color: rgba(226,244,240,0.82); }
.t-line.success .t-prefix { color: #28c840; }
.t-line.success .t-text { color: rgba(57,255,20,0.78); }
.t-line.info .t-prefix, .t-line.info .t-text { color: #00f5d4; }
.t-line.dim .t-text { color: rgba(226,244,240,0.35); }
.t-line.redacted .t-prefix { color: rgba(226,244,240,0.2); }
.t-line.redacted .t-text { color: rgba(226,244,240,0.22); font-style: italic; letter-spacing: 0.04em; }
.t-cursor { color: #00f5d4; animation: blink 1s step-end infinite; }

.pillars-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1px;
  background: rgba(0,245,212,0.07);
  border: 0.5px solid rgba(0,245,212,0.07);
}
.pillar {
  background: #050f12;
  padding: 36px 32px;
  transition: background 0.2s;
}
.pillar:hover { background: #071318; }
.pillar-glyph {
  font-family: 'JetBrains Mono', monospace;
  font-size: 28px;
  color: rgba(0,245,212,0.18);
  font-weight: 300;
  margin-bottom: 14px;
}
.pillar-title {
  font-size: 16px;
  font-weight: 600;
  color: #e2f4f0;
  margin-bottom: 10px;
  letter-spacing: -0.01em;
}
.pillar-text {
  font-size: 13px;
  line-height: 1.75;
  color: rgba(226,244,240,0.5);
  font-weight: 300;
}

.ledger-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1px;
  background: rgba(0,245,212,0.06);
  border: 0.5px solid rgba(0,245,212,0.06);
  margin-bottom: 56px;
}
.ledger-item {
  background: #030b0e;
  padding: 28px 24px;
  transition: background 0.2s;
}
.ledger-item:hover { background: #050f12; }
.ledger-fig {
  font-family: 'JetBrains Mono', monospace;
  font-size: 30px;
  font-weight: 500;
  line-height: 1;
  margin-bottom: 8px;
}
.ledger-label {
  font-size: 13px;
  font-weight: 500;
  color: rgba(226,244,240,0.8);
  margin-bottom: 6px;
}
.ledger-note {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: rgba(226,244,240,0.35);
  line-height: 1.6;
  margin-bottom: 14px;
}
.ledger-bar-track {
  height: 2px;
  background: rgba(226,244,240,0.06);
  border-radius: 2px;
  overflow: hidden;
}
.ledger-bar-fill { height: 100%; border-radius: 2px; }

.diagnosis-quote {
  max-width: 680px;
  margin: 0 auto;
  text-align: center;
}
.dq-mark {
  font-family: 'Playfair Display', serif;
  font-size: 52px;
  color: rgba(0,245,212,0.25);
  line-height: 1;
}
.diagnosis-quote blockquote {
  font-family: 'Playfair Display', serif;
  font-style: italic;
  font-size: 18px;
  line-height: 1.65;
  color: rgba(226,244,240,0.7);
  margin: 4px 0 16px;
}
.dq-attr {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: rgba(0,245,212,0.45);
  letter-spacing: 0.1em;
}

.laws-grid {
  display: grid;
  grid-template-columns: repeat(2,1fr);
  gap: 1px;
  background: rgba(0,245,212,0.07);
}
@media (max-width:768px) { .laws-grid { grid-template-columns:1fr; } }
.law-card {
  display: flex; gap: 20px;
  padding: 32px 36px;
  background: #030b0e;
  transition: background 0.2s;
}
.law-card:hover { background: #050f12; }
.law-numeral {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: rgba(0,245,212,0.35);
  flex-shrink: 0;
  padding-top: 2px;
  width: 20px;
  letter-spacing: 0.06em;
}
.law-name {
  font-size: 15px;
  font-weight: 600;
  color: rgba(226,244,240,0.9);
  margin-bottom: 8px;
  letter-spacing: -0.01em;
}
.law-desc {
  font-size: 13px;
  line-height: 1.65;
  color: rgba(226,244,240,0.45);
  font-weight: 300;
}

.mission-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1px;
  background: rgba(0,245,212,0.06);
}
.mission-card {
  display: flex; gap: 20px;
  padding: 32px 28px;
  background: #030b0e;
  transition: background 0.2s;
}
.mission-card:hover { background: #050f12; }
.mission-num {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: rgba(0,245,212,0.3);
  letter-spacing: 0.1em;
  padding-top: 3px;
  flex-shrink: 0;
  width: 26px;
}
.mission-title {
  font-size: 15px;
  font-weight: 600;
  color: #e2f4f0;
  margin-bottom: 8px;
  letter-spacing: -0.01em;
}
.mission-text {
  font-size: 13px;
  line-height: 1.7;
  color: rgba(226,244,240,0.45);
  font-weight: 300;
}

/* ─── REACTIVE RESEARCH SYSTEMS ─────────────────────────── */

.rrs-section .eyebrow { color: rgba(226,244,240,0.3); }
.rrs-eyebrow-dot {
  width: 5px; height: 5px;
  border-radius: 50%;
  background: rgba(226,244,240,0.2);
  flex-shrink: 0;
}
.rrs-lede { color: rgba(226,244,240,0.4); }

.rrs-grid {
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 48px;
  align-items: start;
  margin-bottom: 44px;
}
@media (max-width:768px) {
  .rrs-grid { grid-template-columns: 1fr; }
}

.rrs-classified-panel {
  background: rgba(226,244,240,0.02);
  border: 0.5px solid rgba(226,244,240,0.07);
  border-radius: 4px;
  padding: 24px 20px;
  position: relative;
}
.rrs-panel-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  color: rgba(226,244,240,0.25);
  letter-spacing: 0.18em;
  margin-bottom: 20px;
}
.rrs-redact-rows { display: flex; flex-direction: column; gap: 14px; }
.rrs-redact-row { display: flex; flex-direction: column; gap: 6px; }
.rrs-redact-key {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  color: rgba(226,244,240,0.22);
  letter-spacing: 0.14em;
}
.rrs-redact-bars { display: flex; gap: 6px; flex-wrap: wrap; }
.rrs-redact-bar {
  height: 10px;
  background: rgba(226,244,240,0.09);
  border-radius: 2px;
  display: inline-block;
}
.rrs-panel-stamp {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  letter-spacing: 0.22em;
  color: rgba(226,244,240,0.1);
  border: 0.5px solid rgba(226,244,240,0.1);
  display: inline-block;
  padding: 4px 10px;
  margin-top: 20px;
  transform: rotate(-3deg);
}

.rrs-caps { display: flex; flex-direction: column; }
.rrs-cap {
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 16px;
  padding: 22px 0;
  border-bottom: 0.5px solid rgba(226,244,240,0.04);
}
.rrs-cap-glyph {
  font-family: 'JetBrains Mono', monospace;
  font-size: 18px;
  color: rgba(226,244,240,0.15);
  padding-top: 2px;
}
.rrs-cap-label {
  font-size: 14px;
  font-weight: 600;
  color: rgba(226,244,240,0.75);
  margin-bottom: 5px;
  letter-spacing: -0.01em;
}
.rrs-cap-text {
  font-size: 13px;
  line-height: 1.7;
  color: rgba(226,244,240,0.38);
  font-weight: 300;
}

.rrs-terminal {
  border-color: rgba(226,244,240,0.07);
  margin-bottom: 28px;
}

.rrs-closing-note {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: rgba(226,244,240,0.2);
  letter-spacing: 0.08em;
  line-height: 1.8;
  border-left: 0.5px solid rgba(226,244,240,0.08);
  padding-left: 18px;
  max-width: 560px;
}

/* ─── END RRS ─────────────────────────────────────────────── */

.closing {
  padding: 110px 0;
  text-align: center;
  position: relative;
  overflow: hidden;
}
.closing-glow {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%,-50%);
  width: 600px; height: 600px;
  background: radial-gradient(circle,rgba(0,245,212,0.05) 0%,transparent 70%);
  pointer-events: none;
}
.closing-eyebrow {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.18em;
  color: rgba(0,245,212,0.45);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.closing-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(36px,5vw,64px);
  font-weight: 700;
  color: #e2f4f0;
  line-height: 1.12;
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
}
.closing-body {
  font-size: 16px;
  line-height: 1.75;
  color: rgba(226,244,240,0.5);
  max-width: 540px;
  margin: 0 auto 44px;
  font-weight: 300;
  position: relative;
  z-index: 1;
}
.closing-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;
}
.btn-teal {
  display: inline-flex;
  align-items: center;
  padding: 14px 32px;
  background: #00f5d4;
  color: #030b0e;
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-decoration: none;
  transition: opacity 0.2s, transform 0.2s;
}
.btn-teal:hover { opacity: 0.88; transform: translateY(-2px); }
.btn-ghost-teal {
  display: inline-flex;
  align-items: center;
  padding: 14px 32px;
  background: transparent;
  color: rgba(226,244,240,0.55);
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.06em;
  border: 0.5px solid rgba(226,244,240,0.18);
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
}
.btn-ghost-teal:hover { border-color: rgba(0,245,212,0.4); color: #00f5d4; }

.page-footer {
  padding: 36px 0;
  background: #020709;
  border-top: 0.5px solid rgba(0,245,212,0.08);
  text-align: center;
}
.footer-text {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: rgba(226,244,240,0.25);
  letter-spacing: 0.06em;
}
.footer-text a {
  color: rgba(0,245,212,0.4);
  text-decoration: none;
  transition: color 0.2s;
}
.footer-text a:hover { color: #00f5d4; }
</style>